import { IRemoteTileset, ITileset, MapFileType } from '~/client/graph'
import Basemap from '~/client/src/shared/models/Basemap'
import Sitemap from '~/client/src/shared/models/Sitemap'
import { isTilesetReady } from '~/client/src/shared/utils/tilesetReadiness'

import type { MapFileOnGlobe } from '../SitemapHelpers/components/GlobeViewFiles/GlobeViewFiles'
import { MapboxSourceType } from './mapboxConstants'
import type { ISitemapWithBasemap } from './MapBoxViewer.store'

/** Everything a globe's map-file reference has to be resolved against. */
export interface IMapFileLookups {
  getSitemap: (id: string) => Sitemap
  getBasemap: (id: string) => Basemap
  getTileset: (id: string) => ITileset
  getRemoteTileset: (id: string) => IRemoteTileset
}

/** One map file on a globe, whatever its kind: ids are only unique within a kind. */
export const mapFileKey = ({ type, file }: MapFileOnGlobe): string =>
  `${type}:${
    type === MapFileType.Sitemap
      ? (file as ISitemapWithBasemap).sitemap.id
      : (file as ITileset | IRemoteTileset).id
  }`

// The extensions an uploaded plan or tileset arrives with.
const FILE_EXTENSION =
  /\.(pdf|png|jpe?g|tiff?|gif|webp|svg|geojson|json|kmz|kml|mbtiles|zip|dwg|dxf)$/i

/** The name a map file is listed by: its own name without its file extension. Sheet numbers such
 * as "A1.01" keep their dots. */
export const mapFileDisplayName = (name: string): string =>
  (name || '').replace(FILE_EXTENSION, '')

/** A tileset is drawable as a vector or a raster source; no other kind has layers. */
export const isTilesetDrawable = (
  tile: Pick<ITileset, 'mapboxData'>,
): boolean =>
  tile.mapboxData?.type === MapboxSourceType.Vector ||
  tile.mapboxData?.type === MapboxSourceType.Raster

/** A plan is drawable once it is placed on the ground and its image has a source. */
export const isPlanDrawable = ({
  sitemap,
  basemap,
}: {
  sitemap?: Pick<Sitemap, 'geoCorners'>
  basemap?: { source?: string }
}): boolean => !!sitemap?.geoCorners && !!basemap?.source

/**
 * Turns a globe's map-file references into the files themselves, dropping any that cannot be
 * drawn: a sitemap that is no longer loaded, a tileset still processing, a remote tileset that
 * has gone. An unresolvable reference is skipped rather than emitted, because the consumer
 * switches on `type` and would dereference a hole.
 */
export const resolveMapFilesOnGlobe = (
  mapFiles: { entityType: MapFileType; entityId: string }[],
  lookups: IMapFileLookups,
): MapFileOnGlobe[] =>
  (mapFiles ?? []).reduce<MapFileOnGlobe[]>((resolved, f) => {
    switch (f.entityType) {
      case MapFileType.Sitemap: {
        const sitemap = lookups.getSitemap(f.entityId)
        if (sitemap) {
          resolved.push({
            type: MapFileType.Sitemap,
            file: {
              sitemap,
              basemap: lookups.getBasemap(sitemap.basemapId),
            },
          })
        }
        return resolved
      }
      case MapFileType.Tileset: {
        const tileset = lookups.getTileset(f.entityId)
        // Still processing means no tiles to draw yet, so it is not on the globe.
        if (isTilesetReady(tileset)) {
          resolved.push({ type: MapFileType.Tileset, file: tileset })
        }
        return resolved
      }
      case MapFileType.RemoteTileset: {
        const remote = lookups.getRemoteTileset(f.entityId)
        if (remote) {
          resolved.push({ type: MapFileType.RemoteTileset, file: remote })
        }
        return resolved
      }
      default:
        // MapFileType is generated from the schema; a member added there arrives here as something
        // this app cannot draw, so it is skipped rather than pushed as a hole.
        return resolved
    }
  }, [])
